import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { LogIn, Trophy, Info } from "lucide-react";
import { IMAGES } from "../lib/config";
import Countdown from "./Countdown";
import { ChevronDown, Flame, CalendarDays } from "lucide-react";

export default function Hero({ onRegister, onOpenLookup }) {
  const nav = useNavigate();
  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-black" data-testid="hero-section">
      {/* Background image with parallax effect */}
      <motion.div
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
        className="absolute inset-0"
      >
        <img
          src={IMAGES.hero}
          alt="Operación Rescate"
          className="w-full h-full object-cover object-center"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
      <div className="absolute inset-0 grain vignette" />

      {/* Top bar */}
      <div className="relative z-20 flex items-center justify-between px-6 lg:px-12 pt-6">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white p-2 shadow-2xl shadow-yellow-500/30 ring-2 ring-yellow-400/50 overflow-hidden flex items-center justify-center flex-shrink-0">
            <img src={IMAGES.logo} alt="" className="w-full h-full object-contain" loading="eager" />
          </div>
          <div className="hidden sm:block">
            <div className="font-heading text-white text-xl tracking-wider whitespace-nowrap">CASA DE ORACIÓN</div>
            <div className="text-yellow-400 text-[10px] uppercase tracking-[0.3em] whitespace-nowrap">Ven y Ve · Iglesia del Nazareno</div>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="ghost"
            onClick={() => nav("/ganadores")}
            data-testid="nav-winners-button"
            className="bg-yellow-400/10 border border-yellow-400/40 text-yellow-400 hover:bg-yellow-400 hover:text-black font-bold tracking-wide px-3 sm:px-4"
          >
            <Trophy className="w-4 h-4 mr-1.5 sm:mr-2" />
            <span className="text-xs sm:text-sm">Ganadores</span>
          </Button>
          <Button
            variant="ghost"
            onClick={onOpenLookup}
            data-testid="nav-my-account-button"
            className="bg-yellow-400/10 border border-yellow-400/40 text-yellow-400 hover:bg-yellow-400 hover:text-black font-bold tracking-wide px-3 sm:px-4"
          >
            <LogIn className="w-4 h-4 mr-1.5 sm:mr-2" />
            <span className="text-xs sm:text-sm">Mi cuenta</span>
          </Button>
          <Button
            variant="ghost"
            onClick={onRegister}
            data-testid="nav-register-button"
            className="text-white hover:text-yellow-400 hover:bg-white/5 font-semibold tracking-wide hidden sm:flex"
          >
            Inscríbete →
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-6 lg:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-4"
        >
          <span className="inline-block px-4 py-2 border border-yellow-400/40 bg-yellow-400/10 backdrop-blur-sm text-yellow-400 text-xs sm:text-sm uppercase tracking-[0.4em] font-bold">
            Campamento de Jóvenes
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="font-heading uppercase text-white text-6xl sm:text-8xl lg:text-[10rem] leading-[0.85] tracking-tighter"
          data-testid="hero-title"
        >
          Operación
        </motion.h1>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="font-heading uppercase text-gradient-gold text-7xl sm:text-9xl lg:text-[12rem] leading-[0.85] tracking-tighter -mt-2"
        >
          Rescate
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-6 text-white/90 text-base sm:text-lg lg:text-xl uppercase tracking-[0.3em] font-light max-w-2xl"
        >
          Un encuentro que puede <span className="text-yellow-400 font-semibold">cambiar tu vida</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-12"
        >
          <Countdown />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-12 flex flex-col sm:flex-row gap-4 items-center"
        >
          <Button
            size="lg"
            onClick={onRegister}
            data-testid="hero-cta-register"
            className="btn-cinematic bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-base sm:text-lg px-10 py-7 rounded-none uppercase tracking-widest"
          >
            Reserva tu Espacio
          </Button>
          <a
            href="#actividades"
            className="text-white/70 hover:text-yellow-400 transition-colors text-sm uppercase tracking-widest font-semibold"
            data-testid="hero-link-activities"
          >
            Ver actividades ↓
          </a>
          <Button
            variant="outline"
            onClick={() => nav("/info")}
            data-testid="hero-cta-info"
            className="bg-black/40 backdrop-blur-sm border-yellow-400/60 text-yellow-400 hover:bg-yellow-400 hover:text-black font-bold uppercase tracking-widest text-sm px-6 py-6 rounded-none"
          >
            <Info className="w-4 h-4 mr-2" />
            Info & Qué llevar
          </Button>
        </motion.div>

        {/* Prominent date badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.7 }}
          className="mt-8 inline-flex items-center gap-3 px-6 py-3 border-2 border-yellow-400 bg-black/60 backdrop-blur-sm"
          data-testid="hero-date-badge"
        >
          <CalendarDays className="w-5 h-5 text-yellow-400" />
          <div className="font-heading text-white text-2xl sm:text-3xl tracking-wide">
            28 · AGOSTO · 2026
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50"
        >
          <span className="text-[10px] uppercase tracking-[0.4em]">3-6 años $55 · 7-11 años $150 · 12+ años $250</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
}
